<template>
  <v-card
    class="w-100 d-flex align-center justify-center"
  >
    <v-sheet
      v-if="registrationStage == 1"
      class="w-33 pt-3"
    >
      <v-row>
        <v-col
          cols="12"
          align="center"
        >
          <h1 class="text-h5 font-weight-medium mb-0">
            Create an account
          </h1>
        </v-col>
        <v-col
          align="center"
        >
          <v-form
            class="mt-5 mb-5"
            fast-fail
            validate-on="submit lazy input"
            @submit.prevent="(event) => isLearnerInvite
              ? registerLearner()
              : setRegistrationVerifyMobile(true, { name: registrationData.name == null ? fullName : registrationData.name, event })"
          >
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-if="inviteData.institution?.name"
                  :model-value="inviteData.institution?.name"
                  label="Institution"
                  type="email"
                  variant="outlined"
                  readonly
                />
                <v-text-field
                  :model-value="inviteData.invite?.email"
                  label="Email"
                  type="email"
                  variant="outlined"
                  readonly
                  autocomplete="email"
                />
                <role-list
                  :model-value="inviteData.roles"
                  :items="inviteData.roles"
                  readonly
                />

                <!-- LRN: username only -->
                <template v-if="isLearnerInvite">
                  <v-text-field
                    v-model="registrationData.username"
                    label="Username"
                    type="text"
                    variant="outlined"
                    :rules="rules.username"
                    :hint="usernameHint"
                    :error-messages="usernameError ? [usernameError] : []"
                    persistent-hint
                    clearable
                    :loading="checkingUsername"
                    autocomplete="username"
                  />
                  <div v-if="usernameSuggestions.length" class="mt-1 mb-3">
                    <div class="text-caption text-medium-emphasis mb-1">Available new user names:</div>
                    <v-chip-group>
                      <v-chip
                        v-for="s in usernameSuggestions"
                        :key="s"
                        color="primary"
                        variant="outlined"
                        size="small"
                        @click="registrationData.username = s"
                      >
                        {{ s }}
                      </v-chip>
                    </v-chip-group>
                  </div>
                </template>

                <!-- Non-LRN: full name + optional username + password -->
                <template v-else>
                  <v-text-field
                    v-if="registrationData.name == null"
                    v-model="fullName"
                    label="Full name"
                    type="text"
                    variant="outlined"
                    :rules="rules.name"
                    clearable
                  />
                  <v-text-field
                    v-else
                    :model-value="registrationData.name"
                    label="Full name"
                    type="text"
                    variant="outlined"
                    readonly
                  />
                  <template v-if="[null, 'password'].includes(registrationData.provider)">
                    <v-text-field
                      v-model="registrationData.password"
                      label="Enter your password"
                      type="password"
                      variant="outlined"
                      :rules="rules.password"
                      clearable
                      autocomplete="new-password"
                    />
                    <v-text-field
                      v-model="registrationData.confirmPassword"
                      label="Confirm your password"
                      type="password"
                      variant="outlined"
                      :rules="rulesConfirmPassword"
                      :disabled="registrationData.password == null"
                      clearable
                      autocomplete="new-password"
                    />
                  </template>
                </template>
              </v-col>
              <v-col
                cols="12"
                class="mt-2"
              >
                <v-btn
                  type="submit"
                  elevation="7"
                  rounded="lg"
                  class="bg-primary w-100 text-none"
                  variant="elevated"
                  :loading="getIsLoadingAuthentication"
                  :disabled="
                    getErrorAuthentication != null ||
                    !!usernameError ||
                    isCheckingUsername ||
                    (isLearnerInvite && !registrationData.username)
                  "
                >
                  Continue
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
      <template v-if="!isLearnerInvite">
        <v-divider gradient>
        <span
          class="font-weight-light text-no-wrap"
          :class="{ 'text-disabled': getErrorAuthentication != null || getRegistrationName != null}"
        >
          OR
        </span>
      </v-divider>
      <v-row
        align="center"
        justify="center"
        class="mt-1"
      >
        <v-col
          cols="6"
          class="d-flex flex-row-reverse"
        >
          <google-authentication action="invite" />
        </v-col>
        <v-col
          cols="6"
          class="d-flex"
        >
          <v-btn
            block
            class="text-none font-weight-regular py-1"
            variant="outlined"
            size="large"
            rounded="lg"
            min-height="auto"
            height="auto"
            :loading="getIsLoadingAuthentication && getProvider == 'aad'"
            :disabled="getErrorAuthentication != null || getRegistrationName != null"
            :href="`/.auth/logout?post_logout_redirect_uri=/.auth/login/aad?post_login_redirect_uri=/register_user/aad/invite/${inviteData.invite.code}`"
          >
            <div class="d-flex align-center">
              <v-img
                width="45"
                cover
                src="/microsoft_entral_id_logo.svg"
                alt=""
              />
              <span class="text-wrap text-center">Continue with  {{ getAvailableProviders.aad }}</span>
            </div>
          </v-btn>
        </v-col>
      </v-row>
      </template>
    </v-sheet>

    <v-sheet
      v-else-if="registrationStage == 2 && registrationData.isOtpSent == null"
      class="w-33 pt-3"
    >
      <v-row>
        <v-col
          cols="12"
          align="center"
        >
          <h1 class="text-h5 font-weight-medium mb-0">
            Verify your account
          </h1>
        </v-col>
        <v-col
          cols="12"
          align="center"
          class="text-subtitle-2 font-weight-medium"
        >
          Enter your phone number and we'll send you a code
        </v-col>
        <v-col
          align="center"
        >
          <v-form
            class="mt-5 mb-5"
            fast-fail
            validate-on="submit lazy input"
            @submit.prevent="(event) => sendOtp(event, 'phone-verify')"
          >
            <v-row>
              <v-col cols="12">
                <v-phone-input
                  v-model="registrationData.mobile"
                  label="Mobile number"
                  default-country="US"
                  variant="outlined"
                  :rules="rules.mobile"
                  @update:country="setRegistrationCountryCode"
                />
              </v-col>
              <v-col
                cols="12"
                class="mt-2"
              >
                <v-btn
                  id="phone-verify"
                  type="submit"
                  elevation="7"
                  rounded="lg"
                  class="bg-primary w-100 text-none"
                  variant="elevated"
                  :loading="getIsLoadingAuthentication"
                  :disabled="getErrorAuthentication != null"
                >
                  Continue
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          align="center"
        >
          <v-btn
            class="ml-1 text-caption text-primary"
            variant="text"
            density="comfortable"
            @click.stop="setRegistrationVerifyMobile(false)"
          >
            <template #prepend>
              <v-icon>mdi-chevron-left</v-icon>
            </template>
            Back
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>


    <v-sheet
      v-else-if="registrationStage == 2 && registrationData.isOtpSent != null"
      class="w-33 pt-3"
    >
      <v-row>
        <v-col
          cols="12"
          align="center"
        >
          <h1 class="text-h5 font-weight-medium mb-0">
            Verify via SMS
          </h1>
        </v-col>
        <v-col
          cols="12"
          align="center"
          class="text-subtitle-2 font-weight-medium"
        >
          Enter the verification code we just sent to your phone
        </v-col>
        <v-col
          align="center"
        >
          <v-form
            class="mt-5 mb-5"
            fast-fail
            validate-on="submit lazy input"
            @submit.prevent="(event) => verifyOtp(event)"
          >
            <v-row>
              <v-col cols="12">
                <v-otp-input
                  v-model="registrationData.otpCode"
                  length="6"
                  autocomplete="one-time-code"
                  :rules="rules.otp"
                />
              </v-col>
              <v-col
                cols="12"
                class="mt-2"
              >
                <v-btn
                  type="submit"
                  elevation="7"
                  rounded="lg"
                  class="bg-primary w-100 text-none"
                  variant="elevated"
                  :loading="getIsLoadingAuthentication"
                  :disabled="getErrorAuthentication != null"
                >
                  Create your account
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          class="text-subtitle-2 font-weight-medium align-center d-flex flex-column mt-5"
        >
          <v-row align="center">
            <span class="mr-2">Didn't receive the code?</span>

            <v-btn
              id="phone-reverify"
              class="text-primary text-none font-weight-medium px-2"
              variant="text"
              @click.stop="(event) => sendOtp(event, 'phone-reverify')"
            >
              Click to resend
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          align="center"
        >
          <v-btn
            class="ml-1 text-caption text-primary"
            variant="text"
            density="comfortable"
            @click.stop="registrationData.isOtpSent = null"
          >
            <template #prepend>
              <v-icon>mdi-chevron-left</v-icon>
            </template>
            Back
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>
  </v-card>
</template>

<script setup>
import { storeToRefs } from 'pinia'

import { useAuthentication } from '@/stores/auth'
import { useUser } from '@/stores/user'

const authenticationStore = useAuthentication()
const learnerStore = useUser()

const {
  registrationStage,
  inviteData,
  registrationData,
  getIsLoadingAuthentication,
  getErrorAuthentication,
  rules,
  getAvailableProviders,
  getRegistrationName
} = storeToRefs(authenticationStore)

const {
  confirmPassword,
  setRegistrationVerifyMobile,
  setRegistrationCountryCode,
  sendOtp,
  verifyOtp,
  registerLearner
} = authenticationStore

const { isCheckingUsername, usernameHint, usernameError, usernameSuggestions } = storeToRefs(learnerStore)

const fullName = ref(null)
const rulesConfirmPassword = ref([])

const isLearnerInvite = computed(() =>
  inviteData.value?.roles?.some(({ code }) => code == 'LRN') ?? false
)

watch(() => registrationData.value.username, (val) => learnerStore.checkUsername(val))

onBeforeMount(async () => {
  rulesConfirmPassword.value = [
    ...rules.value.password,
    (confirmValue) => confirmPassword(confirmValue, registrationData.value.confirmPassword)
  ]
})
</script>
